import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AuthContext = createContext(null);

// Token storage — fallback when third-party cookies are blocked (Safari/Brave, cross-origin prod)
const getAccessToken = () => localStorage.getItem("auth_token");
const getRefreshToken = () => localStorage.getItem("auth_refresh_token");
const clearTokens = () => {
  localStorage.removeItem("auth_token");
  localStorage.removeItem("auth_refresh_token");
};

// Attach Authorization header on every API request (refresh endpoint uses the refresh token)
axios.interceptors.request.use((config) => {
  if (config.url?.startsWith(API) && !config.headers.Authorization) {
    const token = config.url.includes("/auth/refresh") ? getRefreshToken() : getAccessToken();
    if (token) config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Store tokens returned by auth endpoints; on 401, refresh once and retry
let refreshPromise = null;
axios.interceptors.response.use(
  (res) => {
    const d = res.data;
    if (res.config?.url?.startsWith(API) && d && typeof d === "object" && !(d instanceof Blob)) {
      if (d.token) localStorage.setItem("auth_token", d.token);
      if (d.refresh_token) localStorage.setItem("auth_refresh_token", d.refresh_token);
    }
    return res;
  },
  async (error) => {
    const original = error.config;
    const isAuthRoute = original?.url?.includes("/auth/refresh") || original?.url?.includes("/auth/login") || original?.url?.includes("/auth/logout");
    if (error.response?.status === 401 && original && !original._retried && !isAuthRoute) {
      original._retried = true;
      try {
        refreshPromise = refreshPromise || axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
        await refreshPromise;
        refreshPromise = null;
        if (original.headers) delete original.headers.Authorization;
        return axios(original);
      } catch {
        refreshPromise = null;
        clearTokens();
      }
    }
    return Promise.reject(error);
  }
);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);     // null = checking, false = not auth, object = auth
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    try {
      const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(data);
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
    setUser(data);
    return data;
  };

  const register = async (email, password, name) => {
    const { data } = await axios.post(`${API}/auth/register`, { email, password, name }, { withCredentials: true });
    // Don't set user if email not verified (user needs to verify first)
    if (data.email_verified !== false) {
      setUser(data);
    }
    return data;
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const processGoogleCallback = async (sessionId) => {
    const { data } = await axios.get(`${API}/auth/session?session_id=${sessionId}`, { withCredentials: true });
    setUser(data);
    return data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch { /* ignore */ }
    clearTokens();
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, register, logout, loginWithGoogle, processGoogleCallback, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};
